import { useState, useEffect } from 'react';
import { MapPin, Truck, Navigation, ExternalLink, Copy, CheckCircle, Clock, Activity, Search, Loader2, Gauge, Compass, Link2 } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { getTrucksFromAllProLogsConnections } from '../../services/eldConnectionsService';
import { supabase } from '../../lib/supabase';

interface TruckLocation {
  id: string;
  truckNumber: string;
  driver: string;
  currentLocation: {
    address: string;
    city: string;
    state: string;
    lat: number;
    lng: number;
  };
  status: 'moving' | 'stopped' | 'idle';
  speed: number;
  heading?: number;
  lastUpdate: string;
  loadNumber?: string;
  destination?: string;
  eta?: string;
  eldVehicleId?: string;
  connectionName?: string;
  hosStatus?: {
    driveTimeRemaining: number;
    shiftTimeRemaining: number;
    cycleTimeRemaining: number;
    dutyStatus: string;
  };
}

export function ELDLocationView() {
  const toast = useToast();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [trucks, setTrucks] = useState<TruckLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activityFilter, setActivityFilter] = useState<'all' | 'active' | 'inactive'>('all');

  useEffect(() => {
    fetchTruckLocations();
    const interval = setInterval(fetchTruckLocations, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchTruckLocations = async () => {
    try {
      setError(null);
      
      console.log('[ELD] Fetching trucks from all ProLogs connections...');
      const response = await getTrucksFromAllProLogsConnections();
      console.log('[ELD] ProLogs response:', response);
      
      if (!response.success || !response.trucks) {
        console.error('[ELD] Failed to fetch trucks:', response.error);
        throw new Error(response.error || 'Failed to fetch trucks from ProLogs');
      }
      
      const proLogsTrucks = response.trucks;
      console.log('[ELD] ProLogs trucks count:', proLogsTrucks.length);
      console.log('[ELD] First truck:', proLogsTrucks[0]);
      
      const { data: trucksData, error: trucksError } = await supabase
        .from('trucks')
        .select('id, unit_number, eld_vehicle_id, status');
      
      if (trucksError) {
        console.error('[ELD] Error fetching TMS trucks:', trucksError);
      }
      
      console.log('[ELD] TMS trucks count:', trucksData?.length);
      console.log('[ELD] First TMS truck:', trucksData?.[0]);
      
      const mappedTrucks = proLogsTrucks
        .map((loc: any): TruckLocation | null => {
          const truck = trucksData?.find(t => t.eld_vehicle_id === loc.vehicleId?.toString() || t.eld_vehicle_id === loc.name);
          
          // Use TMS truck data if matched, otherwise use ProLogs data directly
          const truckId = truck?.id || `prologs-${loc.vehicleId}`;
          const truckNumber = truck?.unit_number || loc.name || `Truck ${loc.vehicleId}`;
          
          if (!truck) {
            console.log('[ELD] Showing unmatched ProLogs truck:', { vehicleId: loc.vehicleId, name: loc.name });
          } else {
            console.log('[ELD] Matched truck:', truck.unit_number, 'with ProLogs:', loc.name || loc.vehicleId);
          }
          
          const lastUpdateTime = new Date(loc.timeStamp || loc.time || new Date());
          const now = new Date();
          const minutesAgo = Math.floor((now.getTime() - lastUpdateTime.getTime()) / 60000);
          const hoursAgo = Math.floor(minutesAgo / 60);
          
          let status: 'moving' | 'stopped' | 'idle' = 'idle';
          
          if (hoursAgo < 24) {
            if (loc.speed && loc.speed > 5) {
              status = 'moving';
            } else if (minutesAgo < 30) {
              status = 'stopped';
            }
          }
          
          let lastUpdate = '';
          if (minutesAgo < 1) {
            lastUpdate = 'Just now';
          } else if (minutesAgo < 60) {
            lastUpdate = `${minutesAgo} minute${minutesAgo !== 1 ? 's' : ''} ago`;
          } else if (hoursAgo < 24) {
            lastUpdate = `${hoursAgo} hour${hoursAgo !== 1 ? 's' : ''} ago`;
          } else {
            const daysAgo = Math.floor(hoursAgo / 24);
            lastUpdate = `${daysAgo} day${daysAgo !== 1 ? 's' : ''} ago`;
          }
          
          return {
            id: truckId,
            truckNumber: truckNumber,
            driver: loc.driverName || 'Unknown Driver',
            currentLocation: {
              address: loc.location || `GPS: ${loc.lat?.toFixed(4)}, ${loc.lng?.toFixed(4)}`,
              city: loc.city || 'Unknown',
              state: loc.state || 'Unknown',
              lat: loc.lat || 0,
              lng: loc.lng || 0,
            },
            status,
            speed: Math.round(loc.speed || 0),
            heading: loc.heading,
            lastUpdate,
            eldVehicleId: loc.vehicleId?.toString() || loc.name,
            connectionName: loc._connection_name,
            hosStatus: loc.hosStatus,
          };
        })
        .filter((truck: TruckLocation | null): truck is TruckLocation => truck !== null);
      
      setTrucks(mappedTrucks);
      setLoading(false);
    } catch (err: any) {
      console.error('Error fetching ELD locations:', err);
      setError(err.message || 'Failed to load truck locations');
      setLoading(false);
    }
  };

  const copyTrackingLink = (truckId: string, truckNumber: string) => {
    const trackingUrl = `${window.location.origin}/track/${truckId}`;
    navigator.clipboard.writeText(trackingUrl);
    setCopiedId(truckId);
    toast.success(`Tracking link copied for ${truckNumber}`);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const openInMaps = (lat: number, lng: number) => {
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'moving': return 'bg-green-100 text-green-800 border-green-300';
      case 'stopped': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'idle': return 'bg-slate-100 text-slate-600 border-slate-300';
      default: return 'bg-slate-100 text-slate-600 border-slate-300';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'moving': return <Navigation className="w-4 h-4" />;
      case 'stopped': return <Clock className="w-4 h-4" />;
      case 'idle': return <Activity className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const filteredTrucks = trucks.filter(truck => {
    const matchesSearch = !searchTerm || 
      truck.truckNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      truck.driver.toLowerCase().includes(searchTerm.toLowerCase()) ||
      truck.currentLocation.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
      truck.currentLocation.state.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = activityFilter === 'all' ||
      (activityFilter === 'active' && (truck.status === 'moving' || truck.status === 'stopped')) ||
      (activityFilter === 'inactive' && truck.status === 'idle');
    
    return matchesSearch && matchesFilter;
  });

  const activeTrucks = trucks.filter(t => t.status === 'moving' || t.status === 'stopped').length;
  const movingTrucks = trucks.filter(t => t.status === 'moving').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading ELD truck locations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="text-red-800 font-semibold mb-2">Error Loading ELD Data</h3>
        <p className="text-red-600">{error}</p>
        <button
          onClick={fetchTruckLocations}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">ELD Live Tracking</h1>
            <p className="text-blue-100">Real-time truck locations and Hours of Service from ELD system</p>
          </div>
          <div className="flex gap-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-4 text-center">
              <div className="text-3xl font-bold">{trucks.length}</div>
              <div className="text-sm text-blue-100">Total Trucks</div>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-4 text-center">
              <div className="text-3xl font-bold text-green-300">{activeTrucks}</div>
              <div className="text-sm text-blue-100">Active</div>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-lg px-6 py-4 text-center">
              <div className="text-3xl font-bold text-yellow-300">{movingTrucks}</div>
              <div className="text-sm text-blue-100">Moving</div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by truck number, driver, city, or state..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setActivityFilter('all')}
            className={`px-4 py-3 rounded-lg font-medium transition-colors ${
              activityFilter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
            }`}
          >
            All ({trucks.length})
          </button>
          <button
            onClick={() => setActivityFilter('active')}
            className={`px-4 py-3 rounded-lg font-medium transition-colors ${
              activityFilter === 'active'
                ? 'bg-green-600 text-white'
                : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
            }`}
          >
            Active ({activeTrucks})
          </button>
          <button
            onClick={() => setActivityFilter('inactive')}
            className={`px-4 py-3 rounded-lg font-medium transition-colors ${
              activityFilter === 'inactive'
                ? 'bg-slate-600 text-white'
                : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50'
            }`}
          >
            Idle ({trucks.length - activeTrucks})
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Truck</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Driver</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Location</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Speed</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Source</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Last Update</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredTrucks.map((truck) => (
                <tr key={truck.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-600 text-white p-2 rounded-lg">
                        <Truck className="w-5 h-5" />
                      </div>
                      <span className="text-sm font-bold text-slate-900">{truck.truckNumber}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-slate-700">{truck.driver}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border inline-flex items-center gap-1 ${getStatusColor(truck.status)}`}>
                      {getStatusIcon(truck.status)}
                      {truck.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-slate-900">{truck.currentLocation.city}, {truck.currentLocation.state}</p>
                        <p className="text-xs text-slate-500">{truck.currentLocation.address}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Gauge className="w-4 h-4 text-slate-500" />
                      <span className="text-sm font-semibold text-slate-900">{truck.speed} mph</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {truck.connectionName && (
                      <div className="flex items-center gap-1 text-xs text-slate-500">
                        <Link2 className="w-3 h-3" />
                        <span>{truck.connectionName}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                      <Clock className="w-3 h-3" />
                      <span>{truck.lastUpdate}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openInMaps(truck.currentLocation.lat, truck.currentLocation.lng)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs font-medium"
                        title="View on Map"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Map
                      </button>
                      <button
                        onClick={() => copyTrackingLink(truck.id, truck.truckNumber)}
                        className="flex items-center justify-center p-1.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                        title="Copy Tracking Link"
                      >
                        {copiedId === truck.id ? (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredTrucks.length === 0 && (
        <div className="bg-white rounded-xl shadow-md border border-slate-200 p-12 text-center">
          <Truck className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-900 mb-2">No Trucks Found</h3>
          <p className="text-slate-600">
            {searchTerm 
              ? 'Try adjusting your search criteria'
              : 'No trucks are currently reporting location data from ELD'}
          </p>
        </div>
      )}
    </div>
  );
}
